import { Button } from '@shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpFromBracket, faPlus } from '@fortawesome/pro-light-svg-icons';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface CreateProductButtonProps {
  /** Si true, aplica estilos para estar dentro de EmptyView */
  inline?: boolean;
  onOpenBulkUpload: () => void;
}

/**
 * Componente para el botón de crear producto
 * Maneja la navegación y permisos de creación
 * Puede usarse arriba (con posicionamiento) o dentro de EmptyView
 */
export const CreateProductButton = ({ inline = false, onOpenBulkUpload }: CreateProductButtonProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation('productsModule');
  const { canAccess, getTeamType } = usePermissions();

  // Verificar si el usuario puede crear productos
  const canCreate = canAccess(['tenant', 'tenant_3pl', 'tenant_external'], ['products.create']);

  // Verificar si el usuario es 3PL (no puede acceder a carga masiva)
  const is3pl = getTeamType() === 'tenant_3pl';

  // Si no tiene permisos, no mostrar el botón
  if (!canCreate) {
    return null;
  }

  const buttonSize = inline ? 'sm' : 'lg';

  const handleCreateClick = () => {
    navigate('/products/create');
  };

  const handleBulkUploadClick = () => {
    onOpenBulkUpload();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="primary" size={buttonSize} data-testid="create-product-button">
          {/* Primary Action Label - Etiqueta principal del botón */}
          <span className="flex items-center gap-2">{t('buttons.create')}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44" data-testid="create-product-dropdown">
        <DropdownMenuItem onClick={handleCreateClick} data-testid="create-product-menu-create">
          {/* Menu Item - Crear producto */}
          <div className="flex w-full items-center justify-between">
            <span>{t('buttons.create')}</span>
            <FontAwesomeIcon icon={faPlus} className="w-3.5 h-3.5 text-icons" />
          </div>
        </DropdownMenuItem>
        {/* Opción de carga masiva - No disponible para usuarios 3PL */}
        {!is3pl && (
          <DropdownMenuItem onClick={handleBulkUploadClick} data-testid="create-product-menu-bulk-upload">
            {/* Menu Item - Carga masiva */}
            <div className="flex w-full items-center justify-between">
              <span>{t('buttons.bulk_upload')}</span>
              <FontAwesomeIcon icon={faArrowUpFromBracket} className="w-3.5 h-3.5 text-icons" />
            </div>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
